import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Chip,
  createTheme,
  defaultVariantColorsResolver,
  Drawer,
  Input,
  Menu,
  Modal,
  NavLink,
  NumberInput,
  Popover,
  Select,
  Table,
  Tooltip,
  type CSSVariablesResolver,
  type VariantColorsResolver,
} from '@mantine/core'

/**
 * The "calm instrument" look shared with the phone (UX spec §4.2–4.4): Manrope, warm paper neutrals, one deep teal for
 * action, flat tonal cards. Colour values come from the spec's table so both platforms match.
 */
const FONT = 'Manrope, system-ui, -apple-system, "Segoe UI", sans-serif'

/**
 * Mantine works out filled text colour once, from the light shade, so the dark theme's light teal (teal-3) would get
 * white text. Filled teal instead reads `--mt-on-primary`, which styles.css sets per scheme (onPrimary in §4.3).
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  // "danger": the quiet red of a destructive action (delete, remove), i.e. subtle red, named once here.
  if (input.variant === 'danger') return defaultVariantColorsResolver({ ...input, variant: 'subtle', color: 'red' })
  const colors = defaultVariantColorsResolver(input)
  const primary = input.color === undefined || input.color === 'teal'
  return input.variant === 'filled' && primary ? { ...colors, color: 'var(--mt-on-primary)' } : colors
}

export const theme = createTheme({
  primaryColor: 'teal',
  variantColorResolver,
  primaryShade: { light: 9, dark: 3 },
  autoContrast: true,
  focusRing: 'auto',
  // One focus ring everywhere (§11.2 "Focus"), and `respectReducedMotion` so Mantine's own transitions shorten
  // under `prefers-reduced-motion` (§11.4) without a bespoke reduced-motion prop on every component.
  focusClassName: 'mt-focus-ring',
  respectReducedMotion: true,
  fontFamily: FONT,
  fontFamilyMonospace: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  headings: {
    fontFamily: FONT,
    fontWeight: '600',
    sizes: {
      // Owner decision, 24 Oct 2026 (§12 #5): M3's small-app-bar title size (titleLarge, 22/28), overriding the
      // earlier "keep 24" recommendation. PageHeader's <Title order={1}> is every page's title.
      h1: { fontSize: '22px', lineHeight: '28px' },
      h2: { fontSize: '20px', lineHeight: '28px' },
      h3: { fontSize: '14px', lineHeight: '20px' },
      h4: { fontSize: '16px', lineHeight: '24px' },
      h5: { fontSize: '14px', lineHeight: '20px' },
    },
  },
  spacing: { xs: '8px', sm: '12px', md: '16px', lg: '24px', xl: '32px' },
  radius: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '28px' },
  defaultRadius: 'md',
  colors: {
    gray: ['#F8F7F4', '#F3F2EE', '#E9E8E3', '#E1E0D9', '#C3C2B7', '#A3A29A', '#898781', '#52514E', '#2E2D2B', '#0B0B0B'],
    dark: ['#F0EFEC', '#C3C2B7', '#A3A29A', '#898781', '#383835', '#2C2C2A', '#1A1A19', '#111110', '#0B0B0A', '#050505'],
  },
  components: {
    Card: Card.extend({ defaultProps: { bg: 'var(--mt-card)', withBorder: true, padding: 'md', radius: 'md' } }),
    Button: Button.extend({ defaultProps: { radius: 'xl' } }),
    // Basic dialogs are 28 px (M3's modal shape, §11.2); the standard spatial spring never overshoots a modal.
    Modal: Modal.extend({
      defaultProps: { radius: 'xl', centered: true, transitionProps: { duration: 220, timingFunction: 'var(--md-sys-motion-spring-default-spatial-standard)' } },
    }),
    // Owner decision, 24 Oct 2026 (§12 #6, #11): M3's 4 dp outlined-field corner, overriding the earlier "keep 8"
    // recommendation. Chips keep the 8 px filter-chip radius.
    Input: Input.extend({ defaultProps: { radius: 'xs' } }),
    Chip: Chip.extend({ defaultProps: { radius: 'sm' } }),
    Drawer: Drawer.extend({ defaultProps: { transitionProps: { duration: 220, timingFunction: 'var(--md-sys-motion-spring-default-spatial-standard)' } } }),
    Tooltip: Tooltip.extend({ defaultProps: { transitionProps: { duration: 150, transition: 'fade' }, openDelay: 200 } }),
    // Either "." or "," types the decimal point (keyboards differ by locale); it shows as "." and only one is allowed.
    NumberInput: NumberInput.extend({ defaultProps: { decimalSeparator: '.', allowedDecimalSeparators: ['.', ','] } }),
    // A pick list always has an answer; the few that may be cleared say allowDeselect.
    Select: Select.extend({ defaultProps: { allowDeselect: false } }),
    // Icon buttons are quiet grey unless they say otherwise (a red delete, a bordered day arrow).
    ActionIcon: ActionIcon.extend({ defaultProps: { variant: 'subtle', color: 'gray' } }),
    Menu: Menu.extend({ defaultProps: { position: 'bottom-end', withinPortal: true, shadow: 'md' } }),
    Popover: Popover.extend({ defaultProps: { shadow: 'md' } }),
    // Navigation rows: the pill of the side navigation and the settings sections.
    NavLink: NavLink.extend({ defaultProps: { variant: 'light' }, styles: { root: { borderRadius: 'var(--mantine-radius-xl)', minHeight: 40 } } }),
    Badge: Badge.extend({ defaultProps: { variant: 'light' } }),
    // Figures line up in every table (§4.7).
    Table: Table.extend({ classNames: { table: 'tabular' } }),
  },
})

/** Surfaces and text roles from §4.3; Mantine's own defaults are cool greys. */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': '#F3F2EE',
    '--mantine-color-text': '#1B1B19',
    '--mantine-color-dimmed': '#52514E',
    '--mantine-color-default-border': '#E1E0D9',
    '--mantine-primary-color-contrast': '#FFFFFF',
  },
  dark: {
    '--mantine-color-body': '#111110',
    '--mantine-color-text': '#F0EFEC',
    '--mantine-color-dimmed': '#C3C2B7',
    '--mantine-color-default-border': '#383835',
    '--mantine-primary-color-contrast': '#00382A',
  },
})
